import { useMount } from '@contra/react-hooks/useMount';
import { useState } from 'react';

/**
 * Using a generic here to allow being flexible with various LayoutModes
 * eg: type LAYOUT_MODE = 'ONE_COLUMN' | 'TWO_COLUMN';
 * eg2: type LAYOUT_MODE = 'ONE_COLUMN' | 'TWO_COLUMN' | 'THREE_COLUMN';
 */
export const useLayoutMode = <LayoutMode>(
  queries: string[],
  values: LayoutMode[],
  defaultValue: LayoutMode,
): LayoutMode => {
  // Array containing a media query list for each query
  const mediaQueryLists = queries.map((matchQuery) =>
    window.matchMedia(matchQuery),
  );

  // Function that gets value based on matching media query
  const getValue = (): LayoutMode => {
    // Get index of first media query that matches
    const index = mediaQueryLists.findIndex((mql) => mql.matches);

    // Return related value or defaultValue if none
    return values[index] ?? defaultValue;
  };

  // State and setter for matched value
  const [value, setValue] = useState<LayoutMode>(getValue);

  useMount(() => {
    // eslint-disable-next-line unicorn/consistent-function-scoping
    const handler = () => setValue(getValue);

    for (const mql of mediaQueryLists) {
      if (typeof mql.addEventListener === 'function') {
        mql.addEventListener('change', handler);
      } else if (typeof mql.addListener === 'function') {
        mql.addListener(handler);
      }
    }

    return () => {
      for (const mql of mediaQueryLists) {
        if (typeof mql.removeEventListener === 'function') {
          mql.removeEventListener('change', handler);
        } else if (typeof mql.removeListener === 'function') {
          mql.removeListener(handler);
        }
      }
    };
  });

  return value;
};
